import { canBehaviourTableLoadMoreLazy } from "@/helpers/behaviourTableLazyLoading.js"
import { computed, nextTick, onUnmounted, ref, watch } from "vue"

/**
 * Infinite scroll for behaviour tables when "All entries" (75) is selected.
 * @param {{
 *   state: Record<string, unknown>,
 *   getVisibleCount: () => number,
 *   onAppend: () => Promise<void>
 * }} options
 */
export function useBehaviourTableLazyScroll({
  state,
  getVisibleCount,
  onAppend
}) {
  const lazySentinelRef = ref(null)
  let lazyObserver = null

  const canLoadMoreLazy = computed(() => canBehaviourTableLoadMoreLazy(state))

  const teardownLazyObserver = () => {
    if (lazyObserver) {
      lazyObserver.disconnect()
      lazyObserver = null
    }
  }

  const appendLazyChunk = async () => {
    if (
      !state.isLazyLoadingMode ||
      state.isLoading ||
      state.isLazyAppendInFlight ||
      !canLoadMoreLazy.value
    ) {
      return
    }

    const previousVisible = getVisibleCount?.() ?? 0
    await onAppend?.()

    if ((getVisibleCount?.() ?? 0) === previousVisible) {
      teardownLazyObserver()
    }
  }

  const setupLazyObserver = () => {
    teardownLazyObserver()
    if (!state.isLazyLoadingMode || !canLoadMoreLazy.value) return

    const el = lazySentinelRef.value
    if (!el || !("IntersectionObserver" in window)) return

    lazyObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry?.isIntersecting) {
          appendLazyChunk()
        }
      },
      { root: null, rootMargin: "80px", threshold: 0.15 }
    )
    lazyObserver.observe(el)
  }

  watch(
    () => [state.isLazyLoadingMode, canLoadMoreLazy.value],
    () => {
      nextTick(() => setupLazyObserver())
    }
  )

  onUnmounted(() => {
    teardownLazyObserver()
  })

  return {
    lazySentinelRef,
    canLoadMoreLazy,
    setupLazyObserver,
    teardownLazyObserver
  }
}
